import { useTranslation } from 'react-i18next';
import type { OrganisationMembership } from '@ghostscreen/contracts';
import { QueryEmptyState } from '../../components/dashboard/QueryEmptyState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';

type AdminSwitchOrganisationCardProps = {
  memberships: OrganisationMembership[] | undefined;
  activeId: string;
  isPending: boolean;
  onSwitch: (organisationId: string) => void;
};

export function AdminSwitchOrganisationCard({
  memberships,
  activeId,
  isPending,
  onSwitch,
}: AdminSwitchOrganisationCardProps) {
  const { t } = useTranslation('admin');
  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      <h2 className="m-0 text-base font-bold text-ink">{t('switchOrg.title')}</h2>
      <p className="m-0 mt-1 text-sm text-muted">{t('switchOrg.description')}</p>

      <div className="mt-4">
        {isPending ? (
          <QueryLoadingBlock />
        ) : (memberships?.length ?? 0) === 0 ? (
          <QueryEmptyState>{t('switchOrg.empty')}</QueryEmptyState>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {memberships?.map((membership) => {
              const active = membership.id === activeId;
              return (
                <li key={membership.id}>
                  <div
                    className={[
                      'flex items-center gap-3 rounded-[12px] border px-3.5 py-3',
                      active ? 'border-[var(--gs-accent)]' : 'border-line',
                    ].join(' ')}
                    style={{
                      background: active ? 'var(--gs-panel2)' : 'var(--gs-panel)',
                    }}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-semibold text-ink">
                          {membership.name}
                        </span>
                        {active ? (
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                            style={{
                              background: 'color-mix(in srgb, var(--gs-ok) 16%, transparent)',
                              color: 'var(--gs-ok)',
                            }}
                          >
                            {t('switchOrg.active')}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted">
                        {t('displayCount', { count: membership.displayCount })} ·{' '}
                        {t('locationCount', { count: membership.locationCount })}
                      </span>
                    </span>
                    {!active ? (
                      <button
                        type="button"
                        onClick={() => onSwitch(membership.id)}
                        className="shrink-0 rounded-[10px] border border-line px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-ink hover:border-[var(--gs-accent)]"
                      >
                        {t('switchOrg.switch')}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
